import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../config/firebase';
import { Film, Loader, Upload, Pencil } from 'lucide-react';
import { HexColorPicker, HexColorInput } from 'react-colorful';
import BuyCreditsModal from '../components/BuyCreditsModal';
import ExerciseBuilder from './ExerciseBuilder';

const TIMER_FORMATS = [
  { value: '10/20|8', label: '10s work / 20s rest', group: 'Work / Rest', rounds: 8, workSecs: 10, restSecs: 20, mode: 'standard' },
  { value: '15/25|6', label: '15s work / 25s rest', group: 'Work / Rest', rounds: 6, workSecs: 15, restSecs: 25, mode: 'standard' },
  { value: '15/45|4', label: '15s work / 45s rest', group: 'Work / Rest', rounds: 4, workSecs: 15, restSecs: 45, mode: 'standard' },
  { value: '20/10|8', label: '20s work / 10s rest', group: 'Work / Rest', rounds: 8, workSecs: 20, restSecs: 10, mode: 'standard' },
  { value: '20/20|6', label: '20s work / 20s rest', group: 'Work / Rest', rounds: 6, workSecs: 20, restSecs: 20, mode: 'standard' },
  { value: '20/40|4', label: '20s work / 40s rest', group: 'Work / Rest', rounds: 4, workSecs: 20, restSecs: 40, mode: 'standard' },
  { value: '25/15|6', label: '25s work / 15s rest', group: 'Work / Rest', rounds: 6, workSecs: 25, restSecs: 15, mode: 'standard' },
  { value: '30/10|6', label: '30s work / 10s rest', group: 'Work / Rest', rounds: 6, workSecs: 30, restSecs: 10, mode: 'standard' },
  { value: '30/30|4', label: '30s work / 30s rest', group: 'Work / Rest', rounds: 4, workSecs: 30, restSecs: 30, mode: 'standard' },
  { value: '30/90|2', label: '30s work / 1:30 rest', group: 'Work / Rest', rounds: 2, workSecs: 30, restSecs: 90, mode: 'standard' },
  { value: '40/20|4', label: '40s work / 20s rest', group: 'Work / Rest', rounds: 4, workSecs: 40, restSecs: 20, mode: 'standard' },
  { value: '45/15|4', label: '45s work / 15s rest', group: 'Work / Rest', rounds: 4, workSecs: 45, restSecs: 15, mode: 'standard' },
  { value: '120/120|1', label: '2:00 work / 2:00 rest', group: 'Work / Rest', rounds: 1, workSecs: 120, restSecs: 120, mode: 'standard' },
  { value: '60/30/60/30/60|fixed', label: '1:00 / 30 / 1:00 / 30 / 1:00', group: 'Fixed sequence', rounds: 1, workSecs: 60, restSecs: 30, mode: 'fixed' },
  { value: '30/30|4|cw2', label: '30/30 continuous (2 exercises)', group: 'Continuous work', rounds: 4, workSecs: 30, restSecs: 0, mode: 'cw2' },
  { value: '20/20/20|4|cw3', label: '20/20/20 continuous (3 exercises)', group: 'Continuous work', rounds: 4, workSecs: 20, restSecs: 0, mode: 'cw3' },
  { value: '240|1|cw1', label: '4:00 continuous (single)', group: 'Continuous work', rounds: 1, workSecs: 240, restSecs: 0, mode: 'cw1' },
];

const DEFAULT_COLORS = {
  accent: '#c8e020',
  progressBar: '#c8e020',
  timerPanel: '#505050',
  restBackground: '#000000',
};

function isLightColor(hex) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return (r * 0.299 + g * 0.587 + b * 0.114) > 128;
}

function VideoBuilder() {
  const { currentUser, isAdmin } = useAuth();
  const [selectedFormat, setSelectedFormat] = useState('30/30|4');
  const [exerciseName, setExerciseName] = useState('');
  const [footageType, setFootageType] = useState('single');
  const [bilateral, setBilateral] = useState(false);
  const [colors, setColors] = useState(() => {
    const saved = localStorage.getItem('videoBuilderColors');
    if (saved) {
      try { return JSON.parse(saved); } catch { return { ...DEFAULT_COLORS }; }
    }
    return { ...DEFAULT_COLORS };
  });
  const [showBrandColorsModal, setShowBrandColorsModal] = useState(false);
  const [recentColors, setRecentColors] = useState(() => {
    const saved = localStorage.getItem('videoBuilderRecentColors');
    if (saved) {
      try { return JSON.parse(saved); } catch { return []; }
    }
    return [];
  });
  const [activePreviewTab, setActivePreviewTab] = useState('gr');
  const [logoFile, setLogoFile] = useState(null);
  const [footageFiles, setFootageFiles] = useState([]);
  const [previewFootageIndex, setPreviewFootageIndex] = useState(0);
  const [generating, setGenerating] = useState(false);
  const [generationStatus, setGenerationStatus] = useState('');
  const [generationError, setGenerationError] = useState('');
  const [generationSuccess, setGenerationSuccess] = useState(false);
  const [videoCredits, setVideoCredits] = useState(null);
  const [showBuyCreditsModal, setShowBuyCreditsModal] = useState(false);
  const [cardLast4, setCardLast4] = useState('');
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [builderTab, setBuilderTab] = useState('interval');

  // Track window width for responsive layout
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Listen for credit balance changes
  useEffect(() => {
    if (!currentUser?.uid) return;
    const unsub = onSnapshot(doc(db, 'users', currentUser.uid), (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        setVideoCredits(data.videoCredits ?? 0);
        setCardLast4(data.savedCard?.last4 || '');
      }
    });
    return unsub;
  }, [currentUser?.uid]);

  useEffect(() => {
    localStorage.setItem('videoBuilderColors', JSON.stringify(colors));
  }, [colors]);

  useEffect(() => {
    localStorage.setItem('videoBuilderRecentColors', JSON.stringify(recentColors));
  }, [recentColors]);

  const logoPreviewUrl = useMemo(() => {
    if (!logoFile) return null;
    const url = URL.createObjectURL(logoFile);
    return url;
  }, [logoFile]);

  useEffect(() => {
    return () => { if (logoPreviewUrl) URL.revokeObjectURL(logoPreviewUrl); };
  }, [logoPreviewUrl]);

  const footagePreviewUrls = useMemo(() => {
    return footageFiles.map(f => f ? URL.createObjectURL(f) : null);
  }, [footageFiles]);

  useEffect(() => {
    return () => { footagePreviewUrls.forEach(url => { if (url) URL.revokeObjectURL(url); }); };
  }, [footagePreviewUrls]);

  const currentFormat = TIMER_FORMATS.find(f => f.value === selectedFormat) || TIMER_FORMATS[8];
  const isContinuous = ['cw1', 'cw2', 'cw3'].includes(currentFormat.mode);
  const isFixed = currentFormat.mode === 'fixed';
  const isSingleRound = currentFormat.rounds === 1;

  const getFootageCount = () => {
    if (currentFormat.mode === 'cw2') return 2;
    if (currentFormat.mode === 'cw3') return 3;
    if (footageType === 'stagger2') return 2;
    if (footageType === 'stagger3') return 3;
    return 1;
  };
  const footageCount = getFootageCount();

  const getFormatInfo = () => {
    if (currentFormat.mode === 'cw1') return 'Continuous — 4:00 single exercise — 4:30 total';
    if (currentFormat.mode === 'cw2') return `${currentFormat.rounds} cycles — 2 exercises, all work — 4:30 total`;
    if (currentFormat.mode === 'cw3') return `${currentFormat.rounds} cycles — 3 exercises, all work — 4:30 total`;
    if (isFixed) return 'Fixed sequence — 1:00 / 30 / 1:00 / 30 / 1:00 — 4:30 total';
    const workDisplay = currentFormat.workSecs >= 60 ? formatDuration(currentFormat.workSecs) : `${currentFormat.workSecs}s`;
    const restDisplay = currentFormat.restSecs >= 60 ? formatDuration(currentFormat.restSecs) : `${currentFormat.restSecs}s`;
    return `${currentFormat.rounds} round${currentFormat.rounds > 1 ? 's' : ''} — ${workDisplay} work, ${restDisplay} rest — 4:30 total`;
  };

  const formatDuration = (secs) => {
    if (secs >= 60) { const m = Math.floor(secs / 60); const s = secs % 60; return `${m}:${s.toString().padStart(2, '0')}`; }
    return `${secs}`;
  };

  const getFormatLabel = () => {
    if (currentFormat.mode === 'cw1') return '4:00 × 1';
    if (isFixed) return '1:00/30/1:00/30/1:00';
    return `${formatDuration(currentFormat.workSecs)}/${formatDuration(currentFormat.restSecs)} × ${currentFormat.rounds}`;
  };

  const getFootageLabel = () => {
    if (footageCount === 1) return bilateral ? 'Bilateral' : 'Single';
    return `${footageCount} exercises`;
  };

  const getInstructions = () => {
    if (currentFormat.mode === 'cw1') return '4:00 continuous work';
    if (currentFormat.mode === 'cw2') return 'All work — 30/30s each';
    if (currentFormat.mode === 'cw3') return 'All work — 20/20/20s each';
    if (isFixed) return '1:00 / 30 / 1:00 / 30 / 1:00';
    return `Work ${currentFormat.workSecs}s / rest ${currentFormat.restSecs}s`;
  };

  const handleFormatChange = (value) => {
    setSelectedFormat(value);
    const format = TIMER_FORMATS.find(f => f.value === value);
    if (format && (['cw1', 'cw2', 'cw3', 'fixed'].includes(format.mode) || format.rounds === 1)) { setFootageType('single'); setBilateral(false); }
    if (format && ['cw1', 'cw2', 'cw3'].includes(format.mode) && activePreviewTab === 'rt') { setActivePreviewTab('wk'); }
    setPreviewFootageIndex(0);
  };


  // ── Footage validation (gate BEFORE upload) ───────────────────
  // Reject anything over 45s or above 1080p so oversized files never upload.
  const MAX_FOOTAGE_SECONDS = 45;
  const validateFootage = (file) => new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.onloadedmetadata = () => {
      URL.revokeObjectURL(url);
      const seconds = video.duration;
      const longer = Math.max(video.videoWidth, video.videoHeight);
      const shorter = Math.min(video.videoWidth, video.videoHeight);
      if (seconds > MAX_FOOTAGE_SECONDS + 0.5) {
        resolve({ ok: false, reason: 'Clips must be 45 seconds or less.' });
      } else if (longer > 1920 || shorter > 1080) {
        resolve({ ok: false, reason: 'Footage must be no greater than 1080p.' });
      } else {
        resolve({ ok: true });
      }
    };
    video.onerror = () => {
      URL.revokeObjectURL(url);
      resolve({ ok: false, reason: 'Could not read that video file. Try a different clip.' });
    };
    video.src = url;
  });

  const handleFootageSelect = async (file, index) => {
    // Clear the input so re-selecting the same file re-triggers onChange
    const input = document.getElementById(`footage-input-${index}`);
    if (input) input.value = '';
    // Functional update — safe against rapid multi-slot selection
    const setSlot = (val) => setFootageFiles(prev => { const f = [...prev]; f[index] = val; return f; });
    if (!file) { setSlot(null); return; }
    setGenerationError('');
    const result = await validateFootage(file);
    if (!result.ok) {
      setGenerationError(result.reason);
      setSlot(null);
      return;
    }
    setSlot(file);
  };

  const uploadFileToS3 = async (file, prefix) => {
    const functions = getFunctions();
    const getUploadUrl = httpsCallable(functions, 'getUploadUrl');
    const timestamp = Date.now();
    const safeName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
    const fileName = `${prefix}${timestamp}_${safeName}`;
    const result = await getUploadUrl({ fileName, contentType: file.type });
    const presignedUrl = result.data.uploadUrl;
    await new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.onload = () => { if (xhr.status >= 200 && xhr.status < 300) resolve(); else reject(new Error(`Upload failed: ${xhr.status}`)); };
      xhr.onerror = () => reject(new Error('Upload network error'));
      xhr.open('PUT', presignedUrl);
      xhr.setRequestHeader('Content-Type', file.type);
      xhr.send(file);
    });
    return result.data.fileName;
  };

  const handleGenerate = async () => {
    if (!exerciseName.trim()) { setGenerationError('Please enter an exercise name'); return; }
    if (!logoFile) { setGenerationError('Please upload a logo'); return; }
    const requiredFootage = footageCount;
    const uploadedFootage = footageFiles.filter(f => f != null);
    if (uploadedFootage.length < requiredFootage) { setGenerationError(`Please upload ${requiredFootage} footage file${requiredFootage > 1 ? 's' : ''}`); return; }

    setGenerating(true); setGenerationError(''); setGenerationSuccess(false);
    try {
      setGenerationStatus('Uploading logo...');
      const logoKey = await uploadFileToS3(logoFile, 'vb_logos/');
      const footageKeys = [];
      for (let i = 0; i < footageCount; i++) {
        setGenerationStatus(`Uploading footage ${footageCount > 1 ? `${i + 1} of ${footageCount}` : ''}...`);
        const key = await uploadFileToS3(footageFiles[i], 'vb_footage/');
        footageKeys.push(key);
      }
      const workTimerKey = `timers/${currentFormat.workSecs}s.mov`;
      const restTimerKey = currentFormat.restSecs > 0 ? `timers/${currentFormat.restSecs}s.mov` : '';
      setGenerationStatus('Starting video generation...');
      const functions = getFunctions();
      const launchVideoBuilder = httpsCallable(functions, 'launchVideoBuilder');
      const result = await launchVideoBuilder({
        exerciseName: exerciseName.trim(), workSecs: currentFormat.workSecs, restSecs: currentFormat.restSecs,
        rounds: currentFormat.rounds, bilateral, accentColor: colors.accent, timerPanelColor: colors.timerPanel,
        restBackgroundColor: colors.restBackground, progressBarColor: colors.progressBar, formatMode: currentFormat.mode,
        footageType, footageKeys, logoKey, timerKey: workTimerKey, restTimerKey,
      });
      if (result.data.success) { setGenerationSuccess(true); setGenerationStatus('Video is being generated! It will appear in your library when ready.'); }
      else { setGenerationError('Failed to start video generation'); }
    } catch (error) { console.error('Generation error:', error); setGenerationError(error.message || 'Failed to generate video'); }
    finally { setGenerating(false); }
  };

  const resetForNextBuild = () => {
    setExerciseName('');
    setLogoFile(null);
    setFootageFiles([]);
    setPreviewFootageIndex(0);
    setGenerationSuccess(false);
    setGenerationError('');
    setGenerationStatus('');
  };

  const accentTextColor = isLightColor(colors.accent) ? '#1a1a1a' : 'white';
  const formatGroups = TIMER_FORMATS.reduce((groups, format) => { if (!groups[format.group]) groups[format.group] = []; groups[format.group].push(format); return groups; }, {});

  const sectionStyle = { padding: '16px 20px', borderBottom: '1px solid #f0f0f0' };
  const sectionLabelStyle = { fontSize: '11px', fontWeight: 500, color: '#999', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '10px' };
  const fieldLabelStyle = { fontSize: '12px', color: '#666', display: 'block', marginBottom: '4px' };
  const inputStyle = { width: '100%', fontSize: '13px', padding: '6px 8px', border: '1px solid #e0e0e0', borderRadius: '8px', background: '#fafafa', color: '#1a1a2e', boxSizing: 'border-box' };

  const PANEL_PERCENT = 33.6;
  const FOOTAGE_PERCENT = 66.4;

  // Mobile view - show simplified page for screens under 1024px
  if (windowWidth < 1024) {
    return (
      <div style={{ padding: '20px', maxWidth: '500px', margin: '0 auto' }}>
        <div style={{ marginBottom: '20px' }}>
          <h1 style={{ fontSize: '20px', fontWeight: 500, margin: 0, color: '#1a1a2e' }}>
            {builderTab === 'interval' ? 'Video Builder' : 'Exercise Builder'}
          </h1>
          <p style={{ fontSize: '13px', color: '#666', margin: '4px 0 0' }}>
            {builderTab === 'interval' ? 'Create branded workout timer videos' : 'Create branded exercise clips for your program sequences'}
          </p>
        </div>

        {/* Mobile tabs */}
        <div style={{
          display: 'flex', gap: '4px', background: 'white', borderRadius: '8px',
          padding: '4px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', marginBottom: '20px',
        }}>
          <button
            onClick={() => setBuilderTab('interval')}
            style={{
              flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px',
              padding: '8px 8px', borderRadius: '6px', fontSize: '12px', fontWeight: 500,
              border: 'none', cursor: 'pointer', transition: 'all 0.15s',
              background: builderTab === 'interval' ? '#E63946' : 'transparent',
              color: builderTab === 'interval' ? 'white' : '#666',
            }}
          >
            <Film size={12} /> Interval
          </button>
          <button
            onClick={() => setBuilderTab('exercise')}
            style={{
              flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px',
              padding: '8px 8px', borderRadius: '6px', fontSize: '12px', fontWeight: 500,
              border: 'none', cursor: 'pointer', transition: 'all 0.15s',
              background: builderTab === 'exercise' ? '#E63946' : 'transparent',
              color: builderTab === 'exercise' ? 'white' : '#666',
            }}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6.5 6.5l11 11M6.5 17.5l11-11M4 9V5h4M20 9V5h-4M4 15v4h4M20 15v4h-4"/></svg> Exercise
          </button>
        </div>

        <div style={{ textAlign: 'center', padding: '32px 20px' }}>
          <div style={{
            width: '64px', height: '64px', borderRadius: '50%', background: '#fff3cd',
            display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px',
          }}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#856404" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/>
            </svg>
          </div>

          <h2 style={{ fontSize: '18px', fontWeight: 500, margin: '0 0 8px', color: '#1a1a2e' }}>Desktop recommended</h2>
          <p style={{ fontSize: '14px', color: '#666', lineHeight: 1.5, margin: '0 0 24px' }}>
            The Video Builder works best on a larger screen. Use a desktop or laptop for the full building experience.
          </p>
        </div>

        {!isAdmin && (
          <div style={{
            background: 'white', borderRadius: '12px', border: '1px solid #eee',
            padding: '20px', textAlign: 'center', marginBottom: '16px',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '4px' }}>
              <Film size={18} color="#28a745" />
              <span style={{ fontSize: '28px', fontWeight: 500, color: '#1a1a2e' }}>{videoCredits ?? 0}</span>
            </div>
            <span style={{ fontSize: '14px', color: '#666' }}>credits remaining</span>
          </div>
        )}

        {!isAdmin && (
          <button
            onClick={() => setShowBuyCreditsModal(true)}
            style={{
              width: '100%', padding: '12px', background: '#dc3545', color: 'white',
              border: 'none', borderRadius: '8px', fontSize: '15px', fontWeight: 600,
              cursor: 'pointer', marginBottom: '20px',
            }}
          >
            Buy credits
          </button>
        )}

        {!isAdmin && (
          <div style={{ background: 'white', borderRadius: '12px', border: '1px solid #eee', padding: '16px 20px' }}>
            <div style={{ fontSize: '12px', fontWeight: 500, color: '#dc3545', letterSpacing: '0.5px', marginBottom: '12px' }}>VIDEO CREDITS FAQ</div>
            <FAQItem question="What are video credits?" answer="Each credit lets you generate one branded exercise video. Credits are purchased separately from your subscription and never expire." />
            <FAQItem question="How much do credits cost?" answer="Packs start at 5 credits for $5. Larger packs offer a lower per-credit price — 250 credits for $150 ($0.60 each)." />
            <FAQItem question="Do credits expire?" answer="No. Credits never expire. Use them whenever you're ready." />
            <FAQItem question="What do I need to provide?" answer="Your exercise footage and your logo. The builder handles the timer overlay, progress bar, and formatting automatically." />
            <FAQItem question="Are credits refundable?" answer="Credits are non-refundable once purchased. Each credit produces a finished video ready for your screens." />
          </div>
        )}

        <BuyCreditsModal
          isOpen={showBuyCreditsModal}
          onClose={() => setShowBuyCreditsModal(false)}
          currentBalance={videoCredits}
          cardLast4={cardLast4}
          onPurchaseComplete={(newBalance) => setVideoCredits(newBalance)}
        />
      </div>
    );
  }

  return (
    <div style={{ padding: '20px 30px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
        <div>
          <h1 style={{ fontSize: '20px', fontWeight: 500, margin: 0, color: '#1a1a2e' }}>
            {builderTab === 'interval' ? 'Video Builder' : 'Exercise Builder'}
          </h1>
          <p style={{ fontSize: '13px', color: '#666', margin: '4px 0 0' }}>
            {builderTab === 'interval'
              ? 'Create branded workout timer videos'
              : 'Create branded exercise clips for your program sequences'
            }
          </p>
        </div>
        <div style={{ display: 'flex', gap: '4px', background: 'white', borderRadius: '8px', padding: '4px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
          <button
            onClick={() => setBuilderTab('interval')}
            style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              padding: '8px 16px', borderRadius: '6px', fontSize: '14px', fontWeight: 500,
              border: 'none', cursor: 'pointer', transition: 'all 0.15s',
              background: builderTab === 'interval' ? '#E63946' : 'transparent',
              color: builderTab === 'interval' ? 'white' : '#666'
            }}
          >
            <Film size={14} /> Interval Builder
          </button>
          <button
            onClick={() => setBuilderTab('exercise')}
            style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              padding: '8px 16px', borderRadius: '6px', fontSize: '14px', fontWeight: 500,
              border: 'none', cursor: 'pointer', transition: 'all 0.15s',
              background: builderTab === 'exercise' ? '#E63946' : 'transparent',
              color: builderTab === 'exercise' ? 'white' : '#666'
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6.5 6.5l11 11M6.5 17.5l11-11M4 9V5h4M20 9V5h-4M4 15v4h4M20 15v4h-4"/></svg> Exercise Builder
          </button>
        </div>
      </div>

      {builderTab === 'exercise' && <ExerciseBuilder />}

      {builderTab === 'interval' && (
      <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr 260px', gap: '20px', alignItems: 'start', maxWidth: '1200px' }}>

        {/* LEFT PANEL */}
        <div style={{ background: 'white', borderRadius: '12px', border: '1px solid #eee', overflow: 'hidden' }}>
          <div style={{ ...sectionStyle, minHeight: '160px' }}>
            <div style={sectionLabelStyle}>Format</div>
            <div style={{ marginBottom: '10px' }}>
              <label style={fieldLabelStyle}>Timer format</label>
              <select value={selectedFormat} onChange={(e) => handleFormatChange(e.target.value)} style={{ ...inputStyle, fontSize: '12px' }}>
                {Object.entries(formatGroups).map(([group, formats]) => (
                  <optgroup key={group} label={group}>{formats.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}</optgroup>
                ))}
              </select>
            </div>
            <div style={{ fontSize: '11px', color: '#999', padding: '6px 8px', background: '#f5f5f5', borderRadius: '6px' }}>{getFormatInfo()}</div>
          </div>
          <div style={sectionStyle}>
            <div style={sectionLabelStyle}>Exercise</div>
            <div style={{ marginBottom: '10px' }}>
              <label style={fieldLabelStyle}>Exercise name</label>
              <input type="text" value={exerciseName} onChange={(e) => setExerciseName(e.target.value)} placeholder="e.g. Cable RDL" style={inputStyle} />
            </div>
            <div style={{ marginBottom: '10px', opacity: (isContinuous || isFixed || isSingleRound) ? 0.35 : 1, pointerEvents: (isContinuous || isFixed || isSingleRound) ? 'none' : 'auto' }}>
              <label style={fieldLabelStyle}>Footage type</label>
              <select value={footageType} onChange={(e) => { setFootageType(e.target.value); if (e.target.value !== 'single') setBilateral(false); setPreviewFootageIndex(0); }} style={inputStyle}>
                <option value="single">Single exercise</option>
                <option value="stagger2">Staggered — 2 exercises</option>
              </select>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px', opacity: (isContinuous || isFixed || isSingleRound || footageType !== 'single') ? 0.35 : 1, pointerEvents: (isContinuous || isFixed || isSingleRound || footageType !== 'single') ? 'none' : 'auto' }}>
              <input type="checkbox" id="bilateral" checked={bilateral} onChange={(e) => setBilateral(e.target.checked)} style={{ width: '16px', height: '16px', accentColor: '#E63946' }} />
              <label htmlFor="bilateral" style={{ fontSize: '12px', color: '#1a1a2e' }}>Bilateral (RIGHT / LEFT alternating)</label>
            </div>
          </div>
          <div style={{ padding: '16px 20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <div style={sectionLabelStyle}>Brand colors</div>
              <div onClick={() => setShowBrandColorsModal(true)} style={{ fontSize: '11px', color: '#E63946', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>
                <Pencil size={11} /> Edit
              </div>
            </div>
            {[{ key: 'accent', label: 'Accent' }, { key: 'progressBar', label: 'Progress bar' }, { key: 'timerPanel', label: 'Timer panel' }, { key: 'restBackground', label: 'Rest background' }].map(({ key, label }) => (
              <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                <div style={{ width: '32px', height: '32px', borderRadius: '6px', border: '1px solid #e0e0e0', background: colors[key], flexShrink: 0 }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '12px', color: '#1a1a2e' }}>{label}</div>
                  <div style={{ fontSize: '11px', color: '#999', fontFamily: 'monospace' }}>{colors[key]}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CENTER — PREVIEW */}
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <div style={{ background: 'white', borderRadius: '12px', border: '1px solid #e0e0e0', overflow: 'hidden' }}>
            {/* Tabs inside card */}
            <div style={{ display: 'flex', gap: '0', borderBottom: '1px solid #e0e0e0' }}>
              {[{ id: 'gr', label: 'Get ready' }, { id: 'wk', label: 'Work' }, { id: 'rt', label: 'Rest' }]
                .filter(tab => !(tab.id === 'rt' && isContinuous))
                .map(tab => (
                <div key={tab.id} onClick={() => setActivePreviewTab(tab.id)} style={{
                  fontSize: '12px', fontWeight: activePreviewTab === tab.id ? 600 : 500, padding: '10px 18px', cursor: 'pointer',
                  color: activePreviewTab === tab.id ? '#1a1a2e' : '#bbb',
                }}>{tab.label}</div>
              ))}
            </div>

            {/* GET READY */}
            {activePreviewTab === 'gr' && (
              <div style={{ width: '100%', aspectRatio: '16/9', display: 'flex', position: 'relative', overflow: 'hidden', background: colors.accent }}>
                <div style={{ width: `${FOOTAGE_PERCENT}%`, background: '#1a1a1a', position: 'relative', display: 'flex', alignItems: 'flex-end', padding: '3% 2.5%' }}>
                  {footagePreviewUrls[0] ? (
                    <video src={footagePreviewUrls[0]} preload="metadata" muted style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} onLoadedMetadata={(e) => { e.target.currentTime = 0.1; }} />
                  ) : (
                    <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0.15 }}><Film size={36} color="white" /></div>
                  )}
                  <div style={{ position: 'absolute', top: '12%', left: '7.5%', width: '10.5%', aspectRatio: '1', borderRadius: '50%', overflow: 'hidden', background: logoPreviewUrl ? 'transparent' : 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.5)' }}>
                    {logoPreviewUrl ? (
                      <img src={logoPreviewUrl} alt="Logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                    ) : (
                      <svg width="60%" height="60%" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="3" width="18" height="18" rx="3" /><circle cx="8.5" cy="8.5" r="1.5" /><path d="M21 15l-5-5L5 21" /></svg>
                    )}
                  </div>
                  <div style={{ position: 'relative', zIndex: 2 }}>
                    <div style={{ fontSize: '11px', fontWeight: 500, letterSpacing: '0.08em', color: colors.accent }}>EXERCISE</div>
                    <div style={{ fontSize: '14px', fontWeight: 500, color: 'white' }}>{exerciseName || 'Exercise Name'}</div>
                  </div>
                </div>
                <div style={{ width: `${PANEL_PERCENT}%`, flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '2%', gap: '3%', background: colors.accent }}>
                  <div style={{ width: '55%', aspectRatio: '1', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <svg viewBox="0 0 52 52" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}><circle cx="26" cy="26" r="22" fill="none" stroke="white" strokeWidth="2.5" strokeDasharray="138" strokeDashoffset="10" strokeLinecap="round" transform="rotate(-90 26 26)" /></svg>
                    <span style={{ fontSize: '22px', fontWeight: 500, color: 'white', position: 'relative', zIndex: 1 }}>29</span>
                  </div>
                  <div style={{ fontSize: '14px', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.1em', color: accentTextColor }}>to start</div>
                  <div style={{ paddingTop: '4%', width: '80%', textAlign: 'center' }}>
                    <div style={{ fontSize: '9px', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em', color: accentTextColor, marginBottom: '2px' }}>Instructions</div>
                    <div style={{ fontSize: '9px', lineHeight: 1.4, color: accentTextColor }}>{getInstructions()}</div>
                  </div>
                </div>
              </div>
            )}

            {/* WORK */}
            {activePreviewTab === 'wk' && (
              <div style={{ width: '100%', aspectRatio: '16/9', display: 'flex', position: 'relative', overflow: 'hidden', background: colors.timerPanel }}>
                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '1.5%', zIndex: 10 }}><div style={{ height: '100%', width: '35%', background: colors.progressBar }} /></div>
                <div style={{ width: `${FOOTAGE_PERCENT}%`, background: '#1a1a1a', position: 'relative', display: 'flex', alignItems: 'flex-end', padding: '3% 2.5%' }}>
                  {footagePreviewUrls[previewFootageIndex] ? (
                    <video key={previewFootageIndex} src={footagePreviewUrls[previewFootageIndex]} preload="metadata" muted style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} onLoadedMetadata={(e) => { e.target.currentTime = 0.1; }} />
                  ) : (
                    <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0.15 }}><Film size={36} color="white" /></div>
                  )}
                  <div style={{ position: 'absolute', top: '12%', left: '7.5%', width: '10.5%', aspectRatio: '1', borderRadius: '50%', overflow: 'hidden', background: logoPreviewUrl ? 'transparent' : 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.5)' }}>
                    {logoPreviewUrl ? (
                      <img src={logoPreviewUrl} alt="Logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                    ) : (
                      <svg width="60%" height="60%" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="3" width="18" height="18" rx="3" /><circle cx="8.5" cy="8.5" r="1.5" /><path d="M21 15l-5-5L5 21" /></svg>
                    )}
                  </div>
                  <div style={{ position: 'relative', zIndex: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', width: '100%' }}>
                    <div>
                      <div style={{ fontSize: '11px', fontWeight: 500, letterSpacing: '0.08em', color: colors.accent }}>EXERCISE</div>
                      <div style={{ fontSize: '14px', fontWeight: 500, color: 'white' }}>{exerciseName || 'Exercise Name'}</div>
                    </div>
                    {bilateral && <div style={{ fontSize: '11px', fontWeight: 500, letterSpacing: '0.08em', color: colors.accent }}>RIGHT</div>}
                  </div>
                </div>
                <div style={{ width: `${PANEL_PERCENT}%`, flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '2%', gap: '3%', background: colors.timerPanel }}>
                  <div style={{ width: '55%', aspectRatio: '1', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <svg viewBox="0 0 52 52" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}><circle cx="26" cy="26" r="22" fill="none" stroke="white" strokeWidth="2.5" strokeDasharray="138" strokeDashoffset="50" strokeLinecap="round" transform="rotate(-90 26 26)" /></svg>
                    <span style={{ fontSize: '22px', fontWeight: 500, color: 'white', position: 'relative', zIndex: 1 }}>22</span>
                  </div>
                  <div style={{ fontSize: '14px', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.1em', color: colors.accent }}>exercise</div>
                </div>
              </div>
            )}

            {/* REST */}
            {activePreviewTab === 'rt' && (
              <div style={{ width: '100%', aspectRatio: '16/9', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', overflow: 'hidden', background: colors.restBackground }}>
                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '1.5%', zIndex: 10 }}><div style={{ height: '100%', width: '55%', background: colors.progressBar }} /></div>
                <div style={{ position: 'absolute', top: '12%', left: '5%', width: '7%', aspectRatio: '1', borderRadius: '50%', overflow: 'hidden', background: logoPreviewUrl ? 'transparent' : 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.5)' }}>
                    {logoPreviewUrl ? (
                      <img src={logoPreviewUrl} alt="Logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                    ) : (
                      <svg width="60%" height="60%" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="3" width="18" height="18" rx="3" /><circle cx="8.5" cy="8.5" r="1.5" /><path d="M21 15l-5-5L5 21" /></svg>
                    )}
                  </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3%' }}>
                  <div style={{ width: '120px', height: '120px', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <svg viewBox="0 0 52 52" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}><circle cx="26" cy="26" r="22" fill="none" stroke="white" strokeWidth="2.5" strokeDasharray="138" strokeDashoffset="80" strokeLinecap="round" transform="rotate(-90 26 26)" /></svg>
                    <span style={{ fontSize: '22px', fontWeight: 500, color: 'white', position: 'relative', zIndex: 1 }}>18</span>
                  </div>
                  <div style={{ fontSize: '16px', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.12em', color: colors.accent }}>REST</div>
                </div>
              </div>
            )}

            {/* Stats bar with Generate button */}
            <div style={{ padding: '10px 14px', borderTop: '1px solid #e0e0e0', display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
              <div><div style={{ fontSize: '11px', color: '#999' }}>Duration</div><div style={{ fontSize: '14px', fontWeight: 500, color: '#1a1a2e' }}>4:30</div></div>
              <div><div style={{ fontSize: '11px', color: '#999' }}>Format</div><div style={{ fontSize: '14px', fontWeight: 500, color: '#1a1a2e' }}>{getFormatLabel()}</div></div>
              <div><div style={{ fontSize: '11px', color: '#999' }}>Footage</div><div style={{ fontSize: '14px', fontWeight: 500, color: '#1a1a2e' }}>{getFootageLabel()}</div></div>
              <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '16px' }}>
                {!isAdmin && videoCredits !== null && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <Film size={16} color={videoCredits <= 0 ? '#999' : videoCredits <= 3 ? '#e67e22' : '#28a745'} />
                    <span style={{ fontSize: '14px', fontWeight: videoCredits <= 0 ? 400 : 600, color: videoCredits <= 0 ? '#999' : videoCredits <= 3 ? '#e67e22' : '#28a745' }}>
                      {videoCredits} credit{videoCredits !== 1 ? 's' : ''}
                    </span>
                    {videoCredits <= 3 && (
                      <span
                        onClick={() => setShowBuyCreditsModal(true)}
                        style={{ fontSize: '12px', color: '#dc3545', cursor: 'pointer', marginLeft: '2px' }}
                      >
                        {videoCredits <= 0 ? 'Buy' : 'Buy more'}
                      </span>
                    )}
                  </div>
                )}
                <button onClick={handleGenerate} disabled={generating || (!isAdmin && videoCredits <= 0)} style={{
                  padding: '8px 24px', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 600,
                  cursor: (generating || (!isAdmin && videoCredits <= 0)) ? 'default' : 'pointer',
                  background: (generating || (!isAdmin && videoCredits <= 0)) ? '#ccc' : '#E63946', color: 'white',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', whiteSpace: 'nowrap',
                  opacity: (!isAdmin && videoCredits <= 0) ? 0.5 : 1,
                }}>
                  {generating && <Loader size={14} style={{ animation: 'spin 1s linear infinite' }} />}
                  {generating ? 'Generating...' : 'Generate video'}
                </button>
              </div>
            </div>
          </div>

          {/* Upload area — small dashed boxes */}
          <div style={{ background: 'white', borderRadius: '12px', border: '1px solid #eee', padding: '12px 16px', marginTop: '12px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: footageCount > 1 ? `repeat(${footageCount + 1}, 1fr)` : '1fr 1fr', gap: '10px' }}>
              <div style={{ position: 'relative', border: `1px ${logoFile ? 'solid' : 'dashed'} ${logoFile ? '#c8e6c9' : '#ccc'}`, borderRadius: '6px', padding: '10px', textAlign: 'center', background: logoFile ? '#f1f8e9' : '#fafafa', cursor: logoFile ? 'default' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', overflow: 'hidden' }} onClick={() => { if (!logoFile) document.getElementById('logo-input').click(); }}>
                <Upload size={14} color={logoFile ? '#2e7d32' : '#999'} style={{ flexShrink: 0 }} />
                <span style={{ fontSize: '12px', color: logoFile ? '#2e7d32' : '#666', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{logoFile ? logoFile.name : 'Upload Logo'}</span>
                <input id="logo-input" type="file" accept="image/png" style={{ display: 'none' }} onChange={(e) => setLogoFile(e.target.files[0] || null)} />
                {logoFile && (
                  <div onClick={(e) => { e.stopPropagation(); setLogoFile(null); }} style={{ position: 'absolute', top: '2px', right: '4px', width: '18px', height: '18px', borderRadius: '50%', background: '#e0e0e0', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: '12px', color: '#666', lineHeight: 1 }}>&times;</div>
                )}
              </div>
              {Array.from({ length: footageCount }, (_, i) => {
                const hasFile = !!footageFiles[i];
                const isActive = hasFile && previewFootageIndex === i && footageCount > 1;
                return (
                <div key={i} style={{ position: 'relative', border: `1px ${hasFile ? 'solid' : 'dashed'} ${isActive ? '#E63946' : hasFile ? '#c8e6c9' : '#ccc'}`, borderRadius: '6px', padding: '10px', textAlign: 'center', background: isActive ? '#fef8f8' : hasFile ? '#f1f8e9' : '#fafafa', cursor: hasFile && footageCount === 1 ? 'default' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', overflow: 'hidden' }} onClick={() => { if (hasFile && footageCount > 1) { setPreviewFootageIndex(i); setActivePreviewTab('wk'); } else if (!hasFile) { document.getElementById(`footage-input-${i}`).click(); } }}>
                  <Upload size={14} color={hasFile ? '#2e7d32' : '#999'} style={{ flexShrink: 0 }} />
                  <span style={{ fontSize: '12px', color: hasFile ? '#2e7d32' : '#666', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{hasFile ? footageFiles[i].name : footageCount === 1 ? 'Upload Footage' : `Footage ${i + 1}`}</span>
                  <input id={`footage-input-${i}`} type="file" accept="video/*" style={{ display: 'none' }} onChange={(e) => handleFootageSelect(e.target.files[0] || null, i)} />
                  {hasFile && (
                    <div onClick={(e) => { e.stopPropagation(); const f = [...footageFiles]; f[i] = null; setFootageFiles(f); if (previewFootageIndex === i) setPreviewFootageIndex(0); }} style={{ position: 'absolute', top: '2px', right: '4px', width: '18px', height: '18px', borderRadius: '50%', background: '#e0e0e0', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: '12px', color: '#666', lineHeight: 1 }}>&times;</div>
                  )}
                </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* RIGHT SIDEBAR */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <div style={{ background: 'white', borderRadius: '12px', border: '1px solid #eee', padding: '16px 20px' }}>
            <div style={sectionLabelStyle}>Build Status</div>
            {!generating && !generationSuccess && !generationError && (
              <div style={{ fontSize: '13px', color: '#999', lineHeight: 1.5 }}>Ready to build. Estimated time: ~2 minutes.</div>
            )}
            {generating && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Loader size={16} style={{ animation: 'spin 1s linear infinite', color: '#E63946' }} />
                <div style={{ fontSize: '13px', color: '#1a1a2e' }}>{generationStatus}</div>
              </div>
            )}
            {generationSuccess && (
              <div>
                <div style={{ fontSize: '13px', color: '#2e7d32', lineHeight: 1.5, marginBottom: '10px' }}>✓ Video is being generated! It will appear in your asset library shortly.</div>
                <button onClick={resetForNextBuild} style={{
                  padding: '6px 16px', fontSize: '12px', fontWeight: 500, border: 'none',
                  borderRadius: '6px', background: '#2e7d32', color: 'white', cursor: 'pointer',
                }}>Build another</button>
              </div>
            )}
            {generationError && (
              <div>
                <div style={{ fontSize: '13px', color: '#c62828', lineHeight: 1.5, marginBottom: '10px' }}>✕ {generationError}</div>
                <button onClick={() => { setGenerationError(''); }} style={{
                  padding: '6px 16px', fontSize: '12px', fontWeight: 500, border: '1px solid #e0e0e0',
                  borderRadius: '6px', background: 'white', color: '#1a1a2e', cursor: 'pointer',
                }}>Dismiss</button>
              </div>
            )}
          </div>
          <div style={{ background: 'white', borderRadius: '12px', border: '1px solid #eee', padding: '16px 20px' }}>
            <div style={sectionLabelStyle}>Tips</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', fontSize: '12px', color: '#666', lineHeight: 1.5 }}>
              <div>Upload <strong>1080p footage</strong> for best quality. The builder will scale and crop to fit.</div>
              <div>Logo should be a <strong>PNG with transparent background</strong>. It will be placed as-is on the video.</div>
              {bilateral && <div><strong>Bilateral mode</strong> will flip the footage and alternate RIGHT / LEFT labels between rounds.</div>}
              {currentFormat.mode === 'cw2' && <div><strong>Continuous 2</strong> alternates between two exercises with no rest period.</div>}
              {currentFormat.mode === 'cw3' && <div><strong>Continuous 3</strong> rotates through three exercises with no rest period.</div>}
            </div>
          </div>

          {/* Video Credits FAQ */}
          {!isAdmin && (
            <div style={{ background: 'white', borderRadius: '12px', border: '1px solid #eee', padding: '16px 20px' }}>
              <div style={sectionLabelStyle}>Video Credits FAQ</div>
              <FAQItem question="What are video credits?" answer="Each credit lets you generate one branded exercise video. Credits are purchased separately from your subscription and never expire." />
              <FAQItem question="How much do credits cost?" answer="Packs start at 5 credits for $5. Larger packs offer a lower per-credit price — 250 credits for $150 ($0.60 each)." />
              <FAQItem question="Do credits expire?" answer="No. Credits never expire. Use them whenever you're ready." />
              <FAQItem question="What do I need to provide?" answer="Your exercise footage and your logo. The builder handles the timer overlay, progress bar, and formatting automatically." />
              <FAQItem question="Are credits refundable?" answer="Credits are non-refundable once purchased. Each credit produces a finished video ready for your screens." />
            </div>
          )}
        </div>
      </div>
      )}

      {showBrandColorsModal && (
        <BrandColorsModal
          currentColors={colors}
          recentColors={recentColors}
          onSave={(newColors) => {
            setColors(newColors);
            const newRecents = [...recentColors];
            Object.values(newColors).forEach(hex => {
              const lower = hex.toLowerCase();
              const idx = newRecents.findIndex(c => c.toLowerCase() === lower);
              if (idx !== -1) newRecents.splice(idx, 1);
              newRecents.unshift(lower);
            });
            setRecentColors(newRecents.slice(0, 6));
            setShowBrandColorsModal(false);
          }}
          onClose={() => setShowBrandColorsModal(false)}
        />
      )}

      <BuyCreditsModal
        isOpen={showBuyCreditsModal}
        onClose={() => setShowBuyCreditsModal(false)}
        currentBalance={videoCredits}
        cardLast4={cardLast4}
        onPurchaseComplete={(newBalance) => setVideoCredits(newBalance)}
      />

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

const COLOR_FIELDS = [
  { key: 'accent', label: 'Accent' },
  { key: 'progressBar', label: 'Progress bar' },
  { key: 'timerPanel', label: 'Timer panel' },
  { key: 'restBackground', label: 'Rest background' },
];

function BrandColorsModal({ currentColors, recentColors, onSave, onClose }) {
  const [draftColors, setDraftColors] = useState({ ...currentColors });
  const [activeKey, setActiveKey] = useState('accent');
  const modalRef = useRef(null);

  const hasChanges = JSON.stringify(draftColors) !== JSON.stringify(currentColors);

  useEffect(() => {
    const handleEsc = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  const handleDraftChange = (key, value) => {
    setDraftColors(prev => ({ ...prev, [key]: value }));
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.4)' }}>
      <div ref={modalRef} style={{ background: 'white', borderRadius: '12px', width: '460px', maxHeight: '90vh', overflow: 'auto', boxShadow: '0 8px 40px rgba(0,0,0,0.2)' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid #f0f0f0' }}>
          <div style={{ fontSize: '15px', fontWeight: 600, color: '#1a1a2e' }}>Brand colors</div>
          <div onClick={onClose} style={{ cursor: 'pointer', fontSize: '18px', color: '#999', lineHeight: 1 }}>&times;</div>
        </div>

        {/* Color cards — 2x2 grid */}
        <div style={{ padding: '16px 20px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          {COLOR_FIELDS.map(({ key, label }) => {
            const isActive = activeKey === key;
            return (
              <div
                key={key}
                onClick={() => setActiveKey(key)}
                style={{
                  border: isActive ? '2px solid #E63946' : '1px solid #e0e0e0',
                  borderRadius: '10px',
                  padding: isActive ? '11px' : '12px',
                  cursor: 'pointer',
                  background: isActive ? '#fef8f8' : 'white',
                  transition: 'border-color 0.15s, background 0.15s',
                }}
              >
                {/* Color block */}
                <div style={{ width: '100%', height: '48px', borderRadius: '6px', background: draftColors[key], border: '1px solid #e0e0e0', marginBottom: '8px' }} />
                <div style={{ fontSize: '12px', fontWeight: 500, color: '#1a1a2e' }}>{label}</div>
                <div style={{ fontSize: '11px', color: '#999', fontFamily: 'monospace' }}>{draftColors[key]}</div>
                {!isActive && <div style={{ fontSize: '10px', color: '#bbb', marginTop: '4px' }}>click to edit</div>}
              </div>
            );
          })}
        </div>

        {/* Active picker */}
        <div style={{ padding: '0 20px 16px' }}>
          <div style={{ fontSize: '11px', fontWeight: 500, color: '#999', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px' }}>
            Editing: {COLOR_FIELDS.find(f => f.key === activeKey)?.label}
          </div>
          <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
            <HexColorPicker
              color={draftColors[activeKey]}
              onChange={(c) => handleDraftChange(activeKey, c)}
              style={{ width: '180px', height: '140px' }}
            />
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div style={{ width: '100%', height: '48px', borderRadius: '6px', background: draftColors[activeKey], border: '1px solid #e0e0e0' }} />
              <HexColorInput
                color={draftColors[activeKey]}
                onChange={(c) => handleDraftChange(activeKey, c)}
                prefixed
                style={{
                  width: '100%', fontSize: '13px', fontFamily: 'monospace', padding: '6px 8px',
                  border: '1px solid #e0e0e0', borderRadius: '6px', background: '#fafafa', color: '#1a1a2e',
                  boxSizing: 'border-box', outline: 'none',
                }}
              />
              {recentColors.length > 0 && (
                <div>
                  <div style={{ fontSize: '10px', color: '#999', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '5px' }}>Recent</div>
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                    {recentColors.map((c, i) => (
                      <div
                        key={i}
                        onClick={() => handleDraftChange(activeKey, c)}
                        title={c}
                        style={{
                          width: '24px', height: '24px', borderRadius: '50%', background: c,
                          border: draftColors[activeKey].toLowerCase() === c.toLowerCase() ? '2px solid #E63946' : '1px solid #e0e0e0',
                          cursor: 'pointer', flexShrink: 0,
                        }}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', padding: '12px 20px', borderTop: '1px solid #f0f0f0' }}>
          <button onClick={onClose} style={{ padding: '8px 20px', fontSize: '13px', fontWeight: 500, border: '1px solid #e0e0e0', borderRadius: '8px', background: 'white', color: '#666', cursor: 'pointer' }}>
            Cancel
          </button>
          <button
            onClick={() => onSave(draftColors)}
            disabled={!hasChanges}
            style={{
              padding: '8px 20px', fontSize: '13px', fontWeight: 600, border: 'none', borderRadius: '8px',
              background: hasChanges ? '#E63946' : '#eee', color: hasChanges ? 'white' : '#bbb',
              cursor: hasChanges ? 'pointer' : 'default',
            }}
          >
            Save colors
          </button>
        </div>
      </div>
    </div>
  );
}

function FAQItem({ question, answer }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ borderBottom: '1px solid #f0f0f0' }}>
      <div
        onClick={() => setOpen(!open)}
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', cursor: 'pointer' }}
      >
        <span style={{ fontSize: '13px', fontWeight: 500, color: '#1a1a2e' }}>{question}</span>
        <span style={{ fontSize: '12px', color: '#999', transition: 'transform 0.2s', transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}>▼</span>
      </div>
      {open && (
        <div style={{ padding: '0 0 12px', fontSize: '12px', color: '#666', lineHeight: 1.5 }}>
          {answer}
        </div>
      )}
    </div>
  );
}

export default VideoBuilder;